import React, { useState, useRef } from 'react';

// Redux
import { useSelector } from 'react-redux';

// Draggable Scroll
import { useDraggable } from 'react-use-draggable-scroll';

// Material
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';

// Helpers
import { Order, stableSort, getComparator } from 'helpers/tableSortLabels';
import { returnDateAndTime } from 'helpers/date';
import { Capitalize } from 'helpers/capitalize';

// Components

// Constants
import CONST_PAYMENT from 'constants/payments';

// Types
import { PaymentRequestInterface } from 'store/ducks/reports/types';
import { ApplicationState } from 'store';

// Styles
import useStyle from './TablePaymentRequest.styles';

interface TablePaymentRequestProps {
  isHistoryDatails?: boolean;
  paymentRequestList?: PaymentRequestInterface[];
}

type Props = TablePaymentRequestProps;

const TablePaymentRequest: React.FC<Props> = ({
  isHistoryDatails,
  paymentRequestList,
}) => {
  const classes = useStyle();
  const parentRef: any = useRef();
  const { events } = useDraggable(parentRef);

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('amount');

  const initialPaymentRequestList = useSelector((state: ApplicationState) =>
    paymentRequestList && paymentRequestList.length
      ? paymentRequestList
      : state.reports.paymentRequestList,
  );

  const transportOperatorSelected = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorSelected,
  );

  const transportOperatorsList = useSelector(
    (state: ApplicationState) =>
      state.transportOperators.transportOperatorsList,
  );

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <TableContainer
      className={classes.tableContainer}
      {...events}
      ref={parentRef}>
      <Table className={classes.table} component={Paper}>
        <TableHead>
          <TableRow>
            <TableCell
              className={isHistoryDatails ? classes.firstTd : ''}
              variant='head'
              sortDirection={orderBy === 'id' ? order : false}>
              <TableSortLabel
                active
                direction={orderBy === 'id' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'id')}
                classes={{ root: classes.sortLabel, icon: classes.sortLabel }}>
                ID
                {orderBy === 'id' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              sortDirection={orderBy === 'value' ? order : false}>
              <TableSortLabel
                active
                direction={orderBy === 'value' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'value')}
                classes={{ root: classes.sortLabel, icon: classes.sortLabel }}>
                Valor
                {orderBy === 'value' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              sortDirection={orderBy === 'status' ? order : false}>
              <TableSortLabel
                active
                direction={orderBy === 'status' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'status')}
                classes={{ root: classes.sortLabel, icon: classes.sortLabel }}>
                Status
                {orderBy === 'status' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              sortDirection={orderBy === 'createdAt' ? order : false}>
              <TableSortLabel
                active
                direction={orderBy === 'createdAt' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'createdAt')}
                classes={{ root: classes.sortLabel, icon: classes.sortLabel }}>
                Data
                {orderBy === 'createdAt' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              sortDirection={orderBy === 'name' ? order : false}>
              <TableSortLabel
                active
                direction={orderBy === 'name' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'name')}
                classes={{ root: classes.sortLabel, icon: classes.sortLabel }}>
                Terminal
                {orderBy === 'name' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            <TableCell
              variant='head'
              sortDirection={orderBy === 'type' ? order : false}>
              <TableSortLabel
                active
                direction={orderBy === 'type' ? order : 'asc'}
                onClick={(event) => handleRequestSort(event, 'type')}
                classes={{ root: classes.sortLabel, icon: classes.sortLabel }}>
                Pagamento
                {orderBy === 'type' ? (
                  <span className={classes.visuallyHidden}>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </span>
                ) : null}
              </TableSortLabel>
            </TableCell>
            {transportOperatorSelected.type === 1 && (
              <TableCell
                variant='head'
                align='left'
                sortDirection={
                  orderBy === 'transportOperatorId' ? order : false
                }>
                <TableSortLabel
                  classes={{
                    root: classes.sortLabel,
                    icon: classes.sortLabel,
                  }}
                  active
                  direction={orderBy === 'transportOperatorId' ? order : 'asc'}
                  onClick={(event) =>
                    handleRequestSort(event, 'transportOperatorId')
                  }>
                  Operação
                  {orderBy === 'transportOperatorId' ? (
                    <span className={classes.visuallyHidden}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {initialPaymentRequestList.length > 0 &&
            stableSort(
              initialPaymentRequestList,
              getComparator(order, orderBy),
            ).map((paymentRequest: PaymentRequestInterface) => (
              <TableRow key={paymentRequest.id}>
                <TableCell>{paymentRequest.id.split('-')[0]}</TableCell>
                <TableCell>R$ {paymentRequest.value.toFixed(2)}</TableCell>
                <TableCell>
                  {CONST_PAYMENT.STATUS.find(
                    ({ KEY }) => KEY === paymentRequest.status,
                  )?.VALUE || '---'}
                </TableCell>
                <TableCell>
                  {paymentRequest.createdAt
                    ? returnDateAndTime(paymentRequest.createdAt)
                    : '---'}
                </TableCell>
                <TableCell>{paymentRequest.name || '---'}</TableCell>
                <TableCell>
                  {Capitalize(
                    CONST_PAYMENT.TYPES.find(
                      ({ KEY }) => KEY === paymentRequest.type,
                    )?.VALUE || '---',
                  )}
                </TableCell>
                {transportOperatorSelected.type === 1 && (
                  <TableCell>
                    {transportOperatorsList.find(
                      (e) => e.id === paymentRequest?.transportOperatorId,
                    )?.name || '---'}
                  </TableCell>
                )}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export { TablePaymentRequest };
export default TablePaymentRequest;
